import { useState, useEffect } from 'react';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { Shield, Key, User, Smartphone, BellRing, Clock, CalendarDays, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { OtpInput } from '@/components/ui/otp-input';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import type { ApiResponse, TimeEntry } from '@/types';
import { getInitials } from '@/lib/utils';

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaMode, setMfaMode] = useState<'idle' | 'setup' | 'disable'>('idle');
  const [mfaSetup, setMfaSetup] = useState<{ qr_code: string; secret: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaErrorKey, setMfaErrorKey] = useState(0);
  const [mfaSecretCopied, setMfaSecretCopied] = useState(false);
  const [mfaError, setMfaError] = useState('');

  const [sessions, setSessions] = useState<{ id: string; device?: string; last_active?: string }[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const [notifPrefs, setNotifPrefs] = useState<Record<string, { in_app: boolean; email: boolean; teams?: boolean }>>({});
  const [notifPrefsLoading, setNotifPrefsLoading] = useState(true);
  const [notifPrefsSaving, setNotifPrefsSaving] = useState(false);
  const [notifPrefsError, setNotifPrefsError] = useState('');

  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(true);
  const [weeklyHours, setWeeklyHours] = useState(0);

  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
  const [calendarCopied, setCalendarCopied] = useState(false);

  useEffect(() => {
    void api
      .get<ApiResponse<{ path?: string; token?: string }>>('/calendar/token')
      .then((res) => {
        if (!res.success || !res.data?.path) return;
        setCalendarFeedUrl(`${window.location.origin}${res.data.path}`);
      })
      .catch(() => setCalendarFeedUrl(''));
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.full_name);
      setPhone(user.phone ?? '');
      setAvatarUrl(user.avatar_url ?? '');
      setMfaEnabled(user.mfa_enabled);
    }
  }, [user]);

  useEffect(() => {
    const loadNotifPrefs = async () => {
      try {
        const res = await api.get<ApiResponse<Record<string, { in_app: boolean; email: boolean; teams?: boolean }>>>('/notifications/preferences');
        if (!res.success) throw new Error(res.error || 'Benachrichtigungseinstellungen konnten nicht geladen werden.');
        setNotifPrefs(res.data ?? {});
      } catch (e) {
        setNotifPrefsError(e instanceof Error ? e.message : 'Benachrichtigungseinstellungen konnten nicht geladen werden.');
      }
      setNotifPrefsLoading(false);
    };
    loadNotifPrefs();
  }, []);

  useEffect(() => {
    const loadTimeEntries = async () => {
      if (!user) return;
      try {
        const today = new Date();
        const weekStart = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const weekEnd = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const res = await api.get<{ data: TimeEntry[]; total: number }>(`/time-entries?user_id=${user.id}&date_from=${weekStart}&date_to=${weekEnd}&limit=100`);
        const entries = res.data ?? [];
        setTimeEntries(entries);
        setWeeklyHours(entries.reduce((s, e) => s + parseFloat(e.hours as any), 0));
      } catch {}
      setTimeEntriesLoading(false);
    };
    loadTimeEntries();
  }, [user]);

  const EVENT_LABELS: Record<string, string> = {
    'activity.assigned': 'Aktivität zugewiesen',
    'activity.status_changed': 'Status geändert',
    'activity.comment_added': 'Neuer Kommentar',
    'activity.deadline_approaching': 'Deadline nähert sich',
    'campaign.status_changed': 'Kampagnenstatus',
    'campaign.content_submitted': 'Content eingereicht',
    'campaign.content_approved': 'Content genehmigt',
    'campaign.content_rejected': 'Content abgelehnt',
    'budget.threshold_reached': 'Budget-Warnung',
    'plan.created': 'Neuer Plan',
    'strategy.created': 'Neue Strategie',
    'team.member_added': 'Neues Teammitglied',
    social_publish_success: 'Social: Veröffentlichung erfolgreich',
    social_publish_failed: 'Social: Veröffentlichung fehlgeschlagen',
    social_token_expired: 'Social: Token abgelaufen',
    social_rate_limited: 'Social: Rate Limit erreicht',
  };

  const togglePref = async (eventType: string, channel: 'in_app' | 'email' | 'teams') => {
    if (notifPrefsSaving) return;
    const current = notifPrefs[eventType] ?? { in_app: true, email: false, teams: false };
    const updated = { ...current, [channel]: !current[channel] };
    setNotifPrefs((p) => ({ ...p, [eventType]: updated }));
    setNotifPrefsSaving(true);
    setNotifPrefsError('');
    try {
      const response = await api.patch<ApiResponse<unknown>>('/notifications/preferences', { [eventType]: updated });
      if (!response.success) throw new Error(response.error || 'Einstellung konnte nicht gespeichert werden.');
    } catch (e) {
      setNotifPrefs((previous) => ({ ...previous, [eventType]: current }));
      setNotifPrefsError(e instanceof Error ? e.message : 'Einstellung konnte nicht gespeichert werden.');
    } finally {
      setNotifPrefsSaving(false);
    }
  };

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const res = await api.get<ApiResponse<{ sessions: { id: string; device?: string; last_active?: string }[] }>>(
          '/auth/sessions'
        );
        if (res.success && (res as any).data?.sessions) {
          setSessions((res as any).data.sessions);
        }
      } catch {
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    };
    loadSessions();
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess(false);
    setProfileLoading(true);
    try {
      await updateProfile({ full_name: name, phone: phone || undefined, avatar_url: avatarUrl || undefined });
      setProfileSuccess(true);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAvatarUpload = async (file: File | null) => {
    if (!file || !user) return;
    setProfileError('');
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'profile');
      formData.append('entity_id', user.id);
      const uploadRes = await api.upload<ApiResponse<{ id: string }>>('/documents/upload', formData);
      if (!uploadRes.data?.id) throw new Error('Upload fehlgeschlagen.');
      const downloadRes = await api.get<ApiResponse<{ url: string }>>(`/documents/${uploadRes.data.id}/download`);
      const url = downloadRes.data?.url;
      if (!url) throw new Error('Download-URL konnte nicht ermittelt werden.');
      setAvatarUrl(url);
      await updateProfile({ avatar_url: url });
      setProfileSuccess(true);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Profilbild konnte nicht hochgeladen werden.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);
    if (newPassword !== confirmPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Das neue Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await api.post<ApiResponse<unknown>>('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (res.success) {
        setPasswordSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError((res as any).error || 'Passwortänderung fehlgeschlagen.');
      }
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Passwortänderung fehlgeschlagen.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetMfaFlow = () => {
    setMfaMode('idle');
    setMfaSetup(null);
    setMfaCode('');
    setMfaError('');
    setMfaSecretCopied(false);
  };

  const handleMfaToggle = async (enabled: boolean) => {
    setMfaError('');
    setMfaCode('');
    if (!enabled) {
      setMfaMode('disable');
      return;
    }
    setMfaLoading(true);
    try {
      const res = await api.post<ApiResponse<{ qr_code: string; secret: string }>>('/auth/mfa/setup');
      if (!res.success || !res.data?.qr_code) throw new Error(res.error);
      setMfaSetup({ qr_code: res.data.qr_code, secret: res.data.secret });
      setMfaMode('setup');
    } catch {
      setMfaError('MFA-Einrichtung konnte nicht gestartet werden.');
    } finally {
      setMfaLoading(false);
    }
  };

  const submitMfaCode = async (code: string) => {
    if (mfaLoading || code.length !== 6) return;
    setMfaError('');
    setMfaLoading(true);
    const turningOn = mfaMode === 'setup';
    try {
      const res = await api.post<ApiResponse<unknown>>(turningOn ? '/auth/mfa/verify' : '/auth/mfa/disable', { code });
      if (!res.success) {
        setMfaError('Code ungültig. Bitte erneut versuchen.');
        setMfaCode('');
        setMfaErrorKey((k) => k + 1);
        return;
      }
      setMfaEnabled(turningOn);
      useAuthStore.setState((st) => (st.user ? { user: { ...st.user, mfa_enabled: turningOn } } : st));
      resetMfaFlow();
    } catch {
      setMfaError('MFA-Einstellung konnte nicht gespeichert werden.');
    } finally {
      setMfaLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary-600 dark:border-t-primary-400" />
      </div>
    );
  }

  const roleNames = (user.roles ?? []).map((r) => r.name);

  return (
    <div className="w-full space-y-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-foreground">Profil & Konto</h1>
      </div>

      {/* Profile header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Avatar className="h-20 w-20">
              {user.avatar_url ? (
                <AvatarImage src={user.avatar_url} alt={user.full_name} />
              ) : null}
              <AvatarFallback className="text-lg">{getInitials(user.full_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-2xl">{user.full_name}</CardTitle>
              <CardDescription className="mt-1">{user.email}</CardDescription>
              <div className="mt-2 flex flex-wrap gap-1">
                {roleNames.length > 0
                  ? roleNames.map((r) => (
                      <Badge key={r} variant="secondary">
                        {r}
                      </Badge>
                    ))
                  : (
                      <Badge variant="secondary">{user.provider}</Badge>
                    )}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Kalender abonnieren
          </CardTitle>
          <CardDescription>
            Persönliche Termine: zugewiesene Aktivitäten und Kampagnen in Ihren Plänen als ICS-Feed (Outlook, Google
            Calendar). Der Link enthält ein geheimes Token — bitte nicht weitergeben.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {calendarFeedUrl ? (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input readOnly value={calendarFeedUrl} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(calendarFeedUrl);
                      setCalendarCopied(true);
                      window.setTimeout(() => setCalendarCopied(false), 2000);
                    } catch {
                      /* ignore */
                    }
                  }}
                >
                  {calendarCopied ? <Check className="mr-2 h-4 w-4 text-success" /> : <Copy className="mr-2 h-4 w-4" />}
                  Kopieren
                </Button>
              </div>
              <p className="text-xs text-muted">
                In Outlook: Kalender öffnen → «Kalender hinzufügen» → «Aus dem Internet». URL oben einfügen.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted">Kalender-Link konnte nicht geladen werden.</p>
          )}
        </CardContent>
      </Card>

      {/* Edit profile form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profil bearbeiten
          </CardTitle>
          <CardDescription>
            Name, Telefon und Profilbild — sichtbar für Kolleginnen und Kollegen in Zuweisungen und Kommentaren.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            {profileError && (
              <p className="text-sm text-danger">{profileError}</p>
            )}
            {profileSuccess && (
              <p className="text-sm text-success">Profil gespeichert.</p>
            )}
            <div className="space-y-2">
              <LabelWithHint
                htmlFor="profile-name"
                label="Name"
                hint="Vollständiger Anzeigename — erscheint in Aktivitäten, Kampagnen und Benachrichtigungen."
              />
              <Input
                id="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={profileLoading}
              />
            </div>
            <div className="space-y-2">
              <LabelWithHint
                htmlFor="profile-phone"
                label="Telefon"
                hint="Optional — für Rückfragen oder SMS, falls Ihre Organisation das nutzt."
              />
              <Input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={profileLoading}
              />
            </div>
            <div className="space-y-2">
              <LabelWithHint
                htmlFor="profile-avatar"
                label="Avatar-URL"
                hint="Direktlink auf ein Bild (HTTPS). Alternativ unten eine Datei hochladen — die URL wird dann gesetzt."
              />
              <Input
                id="profile-avatar"
                type="url"
                placeholder="https://…"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                disabled={profileLoading}
              />
              <div className="space-y-1">
                <Input
                  type="file"
                  accept="image/*"
                  disabled={avatarUploading || profileLoading}
                  onChange={(e) => handleAvatarUpload(e.target.files?.[0] ?? null)}
                />
                <p className="text-xs text-muted">
                  {avatarUploading ? 'Profilbild wird hochgeladen…' : 'Bilddatei wählen, um automatisch hochzuladen.'}
                </p>
              </div>
            </div>
            <Button type="submit" disabled={profileLoading}>
              {profileLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Speichern…
                </span>
              ) : (
                'Speichern'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Passwort ändern
          </CardTitle>
          <CardDescription>
            Mindestlänge und Komplexität richten sich nach den Vorgaben Ihrer Organisation. Nach der Änderung ggf. neu
            anmelden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {passwordError && (
              <p className="text-sm text-danger">{passwordError}</p>
            )}
            {passwordSuccess && (
              <p className="text-sm text-success">Passwort geändert.</p>
            )}
            <div className="space-y-2">
              <Label htmlFor="current-password">Aktuelles Passwort</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={passwordLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={passwordLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Neues Passwort bestätigen</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={passwordLoading}
              />
            </div>
            <Button type="submit" disabled={passwordLoading}>
              {passwordLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Wird geändert…
                </span>
              ) : (
                'Passwort ändern'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* MFA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Zwei-Faktor-Authentifizierung (MFA)
          </CardTitle>
          <CardDescription>
            Zusätzlicher 6-stelliger Code aus einer Authenticator-App bei jeder Anmeldung.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-medium">
              MFA
              {mfaEnabled && mfaMode === 'idle' && <Badge variant="success">Aktiv</Badge>}
            </span>
            <Switch
              checked={mfaMode === 'setup' ? true : mfaMode === 'disable' ? false : mfaEnabled}
              onCheckedChange={handleMfaToggle}
              disabled={mfaLoading || mfaMode !== 'idle'}
            />
          </div>
          {mfaMode === 'idle' && mfaError && <p className="mt-3 text-sm text-danger">{mfaError}</p>}

          {mfaMode !== 'idle' && (
            <div className="mt-5 rounded-lg border border-border bg-background p-5">
              {mfaMode === 'setup' && mfaSetup && (
                <div className="mb-5 flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-5">
                  <img
                    src={mfaSetup.qr_code}
                    alt="MFA QR-Code"
                    className="h-40 w-40 shrink-0 rounded-md border border-border bg-white p-1.5"
                  />
                  <div className="min-w-0 space-y-2 text-sm">
                    <p className="font-medium">1. QR-Code mit der Authenticator-App scannen</p>
                    <p className="text-muted">Oder Schlüssel manuell eingeben:</p>
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard?.writeText(mfaSetup.secret);
                        setMfaSecretCopied(true);
                      }}
                      className="flex max-w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 font-mono text-xs tracking-wider hover:bg-card-hover"
                    >
                      <span className="truncate">{mfaSetup.secret.match(/.{1,4}/g)?.join(' ')}</span>
                      {mfaSecretCopied ? <Check className="h-3.5 w-3.5 shrink-0 text-success" /> : <Copy className="h-3.5 w-3.5 shrink-0 text-muted" />}
                    </button>
                    <p className="pt-1 font-medium">2. Angezeigten 6-stelligen Code eingeben</p>
                  </div>
                </div>
              )}
              {mfaMode === 'disable' && (
                <p className="mb-5 text-center text-sm text-muted">
                  Zum Deaktivieren aktuellen Code aus der Authenticator-App eingeben.
                </p>
              )}

              <OtpInput
                value={mfaCode}
                onChange={(v) => {
                  setMfaCode(v);
                  if (mfaError) setMfaError('');
                }}
                onComplete={(v) => void submitMfaCode(v)}
                disabled={mfaLoading}
                invalid={Boolean(mfaError)}
                errorKey={mfaErrorKey}
                autoFocus
              />
              {mfaError && <p className="mt-3 text-center text-sm text-danger">{mfaError}</p>}

              <div className="mt-5 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={resetMfaFlow} disabled={mfaLoading}>
                  Abbrechen
                </Button>
                <Button
                  size="sm"
                  variant={mfaMode === 'disable' ? 'destructive' : 'default'}
                  onClick={() => void submitMfaCode(mfaCode)}
                  disabled={mfaLoading || mfaCode.length !== 6}
                >
                  {mfaMode === 'disable' ? 'Deaktivieren' : 'Aktivieren'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active sessions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Aktive Sitzungen
          </CardTitle>
          <CardDescription>
            Übersicht aktiver Browser- oder Gerätesitzungen. Bei verdächtiger Aktivität Passwort ändern und MFA nutzen —
            Sitzungen können ggf. zentral beendet werden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionsLoading ? (
            <div className="space-y-2">
              <div className="h-10 animate-pulse rounded bg-card-hover" />
              <div className="h-10 animate-pulse rounded bg-card-hover" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted">
              Keine weiteren Sitzungen geladen.
            </p>
          ) : (
            <ul className="space-y-2">
              {sessions.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between rounded-lg border border-border py-2 px-3"
                >
                  <span className="text-sm">{s.device ?? 'Unbekanntes Gerät'}</span>
                  <span className="text-xs text-muted">
                    {s.last_active ?? '-'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5" />
            Benachrichtigungs-Einstellungen
          </CardTitle>
          <CardDescription>
            Pro Ereignis können In-App, E-Mail und ggf. Teams getrennt geschaltet werden — reduzieren Sie Kanäle, die Sie
            nicht brauchen, um «Alarmmüdigkeit» zu vermeiden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {notifPrefsLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-card-hover" />)}
            </div>
          ) : (
            <div className="space-y-3 overflow-x-auto">
              {notifPrefsError && <p role="alert" className="text-sm text-danger">{notifPrefsError}</p>}
            <div className="min-w-[420px] divide-y divide-border">
              <div className="flex items-center gap-4 pb-2 text-xs font-medium text-muted">
                <span className="flex-1">Ereignis</span>
                <span className="w-16 text-center">In-App</span>
                <span className="w-16 text-center">E-Mail</span>
                <span className="w-16 text-center">Teams</span>
              </div>
              {Object.entries(EVENT_LABELS).map(([eventType, label]) => {
                const pref = notifPrefs[eventType] ?? { in_app: true, email: false, teams: false };
                return (
                  <div key={eventType} className="flex items-center gap-4 py-2.5">
                    <span className="flex-1 text-sm text-muted">{label}</span>
                    <div className="flex w-16 justify-center">
                      <Switch
                        aria-label={`${label}: In-App`}
                        checked={pref.in_app}
                        onCheckedChange={() => togglePref(eventType, 'in_app')}
                        disabled={notifPrefsSaving}
                      />
                    </div>
                    <div className="flex w-16 justify-center">
                      <Switch
                        aria-label={`${label}: E-Mail`}
                        checked={pref.email}
                        onCheckedChange={() => togglePref(eventType, 'email')}
                        disabled={notifPrefsSaving}
                      />
                    </div>
                    <div className="flex w-16 justify-center">
                      <Switch
                        aria-label={`${label}: Teams`}
                        checked={!!pref.teams}
                        onCheckedChange={() => togglePref(eventType, 'teams')}
                        disabled={notifPrefsSaving}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weekly Time Entries */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Meine Zeiteinträge diese Woche
          </CardTitle>
          <CardDescription>
            Gesamt: {weeklyHours.toFixed(1)} Stunden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timeEntriesLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-card-hover" />)}
            </div>
          ) : timeEntries.length === 0 ? (
            <p className="text-sm text-muted">
              Keine Zeiteinträge diese Woche.
            </p>
          ) : (
            <ul className="space-y-2">
              {timeEntries.map((te) => (
                <li
                  key={te.id}
                  className="flex items-center justify-between rounded-lg border border-border py-2 px-3"
                >
                  <div className="min-w-0 flex-1">
                    <span className="truncate text-sm font-medium text-muted">
                      {te.marketing_activities?.title ?? 'Aktivität'}
                    </span>
                    {te.description && (
                      <span className="ml-2 text-xs text-muted">{te.description}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted">{te.date}</span>
                    <Badge variant="secondary">{parseFloat(te.hours as any).toFixed(1)}h</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
